import React, { useState, useRef, useEffect } from 'react';
import Dashboard from './pages/Dashboard';
import Settings from './pages/Settings';
import Watchlist from './pages/Watchlist';
import Assistant from './pages/Assistant';
import Report from './pages/Report';
import Admin from './pages/Admin';
import Control from './pages/Control';
import DecisionLab from './pages/DecisionLab';
import Login from './pages/Login';
import ChangePassword from './pages/ChangePassword';
import api from './services/api';
import FloatingMenu from './components/FloatingMenu';
import ChartHost from './components/ChartWindow';
import ApprovalFlash, { PushButton } from './components/ApprovalFlash';
import TradeSound from './components/TradeSound';
import AutoUpdate from './components/AutoUpdate';
// StrategyIdeaPopup removed 2026-10-13 with the CE+PE strategy
import './App.css';

function App() {
  const [authenticated, setAuthenticated] = useState(false);
  const statusFailsRef = useRef(0);   // consecutive failed status polls (no-flicker header)
  const [currentUser, setCurrentUser] = useState(null);
  const [, setIsApiConfigured] = useState(false);
  const [, setIsLoggedIn] = useState(false);
  const [brokerConnected, setBrokerConnected] = useState(false);
  const [brokerHealth, setBrokerHealth] = useState(null);
  const [tradingMode, setTradingMode] = useState('');
  // REMEMBER THE OPEN PAGE (owner 2026-10-16: "refresh on the Watchlist sends me to home - it should stay on the
  // Watchlist"; then "the address bar should not show the page"): kept only in this browser's storage.
  const PAGES = ['dashboard', 'watchlist', 'assistant', 'report', 'settings', 'admin', 'control'];
  const [currentPage, setCurrentPageState] = useState(() => {
    try { if (window.location.hash) window.history.replaceState(null, '', window.location.pathname); } catch (e) { /* ignore */ }
    try { const v = localStorage.getItem('app_page'); if (PAGES.includes(v)) return v; } catch (e) { /* storage blocked */ }
    return 'dashboard';
  });
  const setCurrentPage = (pg) => {
    setCurrentPageState(pg);
    try { localStorage.setItem('app_page', pg); } catch (e) { /* storage blocked */ }
  };
  const [loading, setLoading] = useState(true);
  const [showChangePassword, setShowChangePassword] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('app_token');
    const savedUser = localStorage.getItem('app_user');
    if (token) {
      api.appVerify(token).then(res => {
        if (res.authenticated) {
          setAuthenticated(true);
          setCurrentUser(res.user || (savedUser ? JSON.parse(savedUser) : null));
        } else {
          localStorage.removeItem('app_token');
          localStorage.removeItem('app_user');
        }
        setLoading(false);
      }).catch(() => {
        // SERVER UNREACHABLE (e.g. a deploy restart) is NOT a logout (owner 2026-10-16: refresh did not open the
        // dashboard): keep the saved login and open the app; the status polls reconnect on their own.
        if (savedUser) {
          setAuthenticated(true);
          try { setCurrentUser(JSON.parse(savedUser)); } catch (e) { /* ignore */ }
        }
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authenticated) return;
    const checkStatus = async () => {
      try {
        const statusRes = await api.getStatus();
        setIsApiConfigured(statusRes.is_api_configured || false);
        setIsLoggedIn(statusRes.is_logged_in || false);
        setBrokerConnected(statusRes.broker_connected || false);
        setBrokerHealth(statusRes.broker_health || null);
        // PER-USER mode (BUGFIX 2026-08-17): the header badge must show THIS account's
        // OWN mode, not the engine's GLOBAL current_mode. A client (or the admin) who
        // selected LIVE saw "PAPER MODE" because current_mode sits at the global 'paper'.
        // status.mode is the logged-in user's own trade_mode; current_mode is the fallback.
        setTradingMode(statusRes.mode || statusRes.current_mode || '');
        statusFailsRef.current = 0;
      } catch (error) {
        // NO FLICKER (user 2026-10-06): one slow/failed poll must not flip the header to
        // "not logged in / broker off" and back 2s later — keep the last good values and
        // only show disconnected after 3 failures in a row.
        statusFailsRef.current += 1;
        if (statusFailsRef.current >= 3) {
          setIsApiConfigured(false);
          setIsLoggedIn(false);
          setBrokerConnected(false);
        }
      }
    };
    checkStatus();
    const interval = setInterval(checkStatus, 5000);   // was 2s: the dashboard already polls status
    return () => clearInterval(interval);
  }, [authenticated]);

  const handleLogin = (token, user) => {
    setAuthenticated(true);
    setCurrentUser(user);
  };

  const handleLogout = async () => {
    // Tell the server to end this session FIRST so the engine marks us offline
    // and stops trading for us — then clear the local login. (Staying logged in
    // keeps the app trading in the background; logout is what makes us offline.)
    try { await api.appLogout(); } catch (e) { /* clear locally regardless */ }
    localStorage.removeItem('app_token');
    localStorage.removeItem('app_user');
    setAuthenticated(false);
    setCurrentUser(null);
    setCurrentPage('dashboard');
  };

  if (loading) {
    return <div className="App"><div className="loading">Loading...</div></div>;
  }

  if (!authenticated) {
    return (
      <div className="App">
        <Login onLogin={handleLogin} />
      </div>
    );
  }

  const isAdmin = currentUser?.role === 'admin';
  const page = (!isAdmin && (currentPage === 'admin' || currentPage === 'control')) ? 'dashboard' : currentPage;

  return (
    <div className="App">
      <header className="app-header">
        <div className="header-content">
          <h1>AI Trading Platform</h1>
          {/* page menu moved to the round floating button (owner 2026-10-13) */}
          <div className="header-right">
            <div className="user-info">
              <span className="user-name">{currentUser?.name || currentUser?.user_id}</span>
              <span className={`role-badge ${currentUser?.role}`}>{currentUser?.role}</span>
              <button className="change-pwd-btn" onClick={() => setShowChangePassword(true)}>Change Password</button>
            </div>
            <div className="connection-status">
              {/* Badge follows the SELECTED trading mode, not broker connectivity */}
              {tradingMode === 'paper' ? (
                <><span className="status-dot" style={{ background: '#ff8800' }}></span><span style={{ color: '#ff8800', fontWeight: 700 }}>PAPER MODE</span></>
              ) : tradingMode === 'live' ? (
                brokerConnected ? (
                  <><span className="status-dot connected"></span><span style={{ color: '#00ff88', fontWeight: 700 }}>LIVE</span></>
                ) : (
                  <><span className="status-dot disconnected"></span><span style={{ color: '#ff4444', fontWeight: 700 }}>LIVE — BROKER OFFLINE</span></>
                )
              ) : (
                <><span className="status-dot" style={{ background: '#666' }}></span><span style={{ color: '#888' }}>…</span></>
              )}
            </div>
            <button className="logout-btn" onClick={handleLogout}>Logout</button>
          </div>
        </div>
        {/* Broker session health — shows in BOTH paper and live modes. An IP change or
            token expiry silently breaks the broker session; this surfaces it loudly
            (scanning needs live quotes from the broker in either mode). */}
        {brokerHealth && brokerHealth.healthy === false && (
          <div style={{
            background: '#3a0d0d', border: '1px solid #ff4444', color: '#ff6b6b',
            padding: '8px 14px', margin: '8px 16px', borderRadius: 6, fontWeight: 600, fontSize: 13,
          }}>
            ⚠ Broker session offline ({brokerHealth.broker}): {brokerHealth.error || 'connection lost'}
            {brokerHealth.needs_relogin ? ' — likely IP change / session expiry. Re-login via Settings.' : ''}
          </div>
        )}
      </header>
      <main className="app-main">
        {/* master 'Not Connected' gate is ADMIN-only — clients always get their
            dashboard (it has its own gentle connect-your-broker banner) */}
        {/* The old admin 'Not Connected' gate is gone (owner 2026-10-16: after a refresh the dashboard did not
            open): every user trades in their own paper book / own broker, and the dashboard shows its own banners. */}
        {(
          <>
            {page === 'dashboard' && <Dashboard />}
            {page === 'watchlist' && <Watchlist />}
            {page === 'assistant' && <Assistant />}
            {page === 'report' && <Report />}
            {page === 'settings' && <Settings />}
            {page === 'admin' && isAdmin && <Admin onClose={() => setCurrentPage('dashboard')} />}
            {page === 'control' && isAdmin && <Control />}
            {page === 'decisions' && isAdmin && <DecisionLab />}
          </>
        )}
      </main>
      <ChartHost />
      {isAdmin ? <ApprovalFlash /> : <PushButton />}
      <TradeSound />
      <AutoUpdate />
      <FloatingMenu
        current={page}
        onSelect={setCurrentPage}
        items={[
          { id: 'dashboard', label: 'Dashboard', icon: '🏠' },
          { id: 'watchlist', label: 'Watchlist', icon: '📈' },
          { id: 'assistant', label: 'Assistant', icon: '🤖' },
          { id: 'report', label: 'Report', icon: '📊' },
          { id: 'settings', label: 'Settings', icon: '⚙️' },
          ...(isAdmin ? [{ id: 'admin', label: 'Admin', icon: '🛡️' }, { id: 'control', label: 'Control', icon: '🎛️' }, { id: 'decisions', label: 'My Decisions', icon: '🧠' }] : []),
        ]}
      />
      {showChangePassword && <ChangePassword onClose={() => setShowChangePassword(false)} />}
    </div>
  );
}

export default App;
